import { Link } from 'react-router-dom';
import GuideLayout from '../../components/guides/GuideLayout';
import GuideCTA from '../../components/guides/GuideCTA';
import { GuideSection, GuideFaqItem, GuideChecklist, GuideNote, SourceLink } from '../../components/guides/GuideParts';

const SLUG = 'how-to-find-out-why-you-were-taken-into-care';

export const WhyTakenIntoCare = () => (
  <GuideLayout slug={SLUG}>
    <GuideSection>
      <p>
        This is one of the most common reasons people request their records at all, and the
        answer is almost always written down somewhere — often in more than one place, in more
        than one form. A short line in a court order, a two-page legal document setting out the
        specific facts relied on, a social worker&apos;s fuller narrative, sometimes a police log
        from the day everything started. This guide walks through where each version usually
        lives, starting with the easiest and most complete source before moving to the ones that
        take more work.
      </p>
    </GuideSection>

    <GuideSection eyebrow="Start here" title="The council file usually has most of it already">
      <p>
        A local authority that took a case to court is a party to those proceedings, which means
        it keeps copies of what it filed and what the court decided — you do not need to go
        anywhere near the court itself for most of this. Ask your{' '}
        <Link to="/guides/how-to-request-your-care-records-from-the-council" className="text-brand underline hover:opacity-90">
          council Subject Access Request
        </Link>{' '}
        to specifically include:
      </p>
      <GuideChecklist
        items={[
          <>
            <strong className="text-ink">The Letter Before Proceedings.</strong> Before a council
            can apply to court for a care order, it has to send parents a formal letter setting
            out its concerns and its intention to apply — a document written specifically to
            explain, in plain terms, why it is considering this. It exists precisely to lay out
            the "why" before anything is decided.
          </>,
          <>
            <strong className="text-ink">The Threshold Statement.</strong> Once an application
            reaches court, the council&apos;s legal team has to file a short document — no more than
            two pages, by the court&apos;s own rules — setting out the specific facts it relies on to
            meet the legal test for a care order (that you were suffering, or likely to suffer,
            significant harm). Short as it is, this is often the single most direct written
            answer to "why" in the entire file.
          </>,
          'Referral and assessment records — who first raised concerns, when, and what the initial and any follow-up assessments found.',
          'Legal planning meeting minutes, where a decision to seek legal advice or go to court was actually made.',
          "Social workers' statements filed with the court — the fuller, narrative version of the threshold facts.",
          'The care order itself, and any care plan filed alongside it.',
        ]}
      />
      <p>
        Name these specifically in your request rather than just asking for "my file" — see our{' '}
        <Link to="/guides/how-to-request-your-care-records-from-the-council" className="text-brand underline hover:opacity-90">
          guide to requesting care records from the council
        </Link>{' '}
        for the general approach.
      </p>
    </GuideSection>

    <GuideNote>
      <p>
        <strong>The threshold test itself is worth knowing.</strong> Under section 31 of the{' '}
        <SourceLink href="https://www.legislation.gov.uk/ukpga/1989/41/section/31">
          Children Act 1989
        </SourceLink>
        , a court can only make a care order if you were suffering, or likely to suffer,
        significant harm, and that harm was attributable to the care being given (or likely to be
        given) not being what a reasonable parent would give — or to you being beyond parental
        control. Every threshold statement is built to answer that specific legal question, which
        is exactly why it tends to be the clearest, most direct document in the file.
      </p>
    </GuideNote>

    <GuideCTA />

    <GuideSection eyebrow="In more urgent cases" title="Police can hold the first page of the story">
      <p>
        Most of the time, social care&apos;s own records are the whole picture. But in cases that
        started as an emergency — a domestic incident, a welfare check, an arrest, a child found
        in immediate danger — the police may have been the first agency involved at all,
        sometimes before a social worker was ever on the scene.
      </p>
      <p>
        Where a constable has reasonable cause to believe a child would otherwise be likely to
        suffer significant harm, they can remove the child to safety without a court order at
        all, for up to{' '}
        <SourceLink href="https://www.legislation.gov.uk/ukpga/1989/41/section/46">
          72 hours
        </SourceLink>{' '}
        — called being taken into <strong className="text-ink">police protection</strong>. The
        law requires the officer to record and communicate the reasons for doing this, both to
        the local authority and, where you were old enough to understand, to you at the time.
        That means a genuine, contemporaneous, first-hand account can exist in police records from
        the very start, in exactly the kind of case where it matters most.
      </p>
      <p>
        Many areas also run early information-sharing between police, health, education and
        social care through what is often called a Multi-Agency Safeguarding Hub (MASH) — another
        reason a concern that started with police contact can be the actual trigger for social
        work involvement, not just a side note within it. See our{' '}
        <Link to="/guides/how-to-request-your-police-records" className="text-brand underline hover:opacity-90">
          guide to requesting police records
        </Link>{' '}
        for how to ask.
      </p>
    </GuideSection>

    <GuideSection eyebrow="Often the clearest single account" title="Cafcass's report">
      <p>
        If your case went to court, an independent Cafcass guardian was very likely appointed to
        represent your interests, and their report is often written in plainer, more narrative
        terms than either the council&apos;s legal documents or the court&apos;s own order — precisely
        because their job was to form and explain an independent view of what was actually going
        on. See our{' '}
        <Link to="/guides/how-to-request-your-cafcass-and-family-court-records" className="text-brand underline hover:opacity-90">
          guide to requesting your Cafcass and family court records
        </Link>
        , which is a straightforward request in its own right.
      </p>
    </GuideSection>

    <GuideSection eyebrow="The harder route" title="Going to the court itself">
      <p>
        The court that made the order keeps its own file too — but, unlike the council and
        Cafcass, that is not something an ordinary Subject Access Request reaches. Getting access
        needs the court&apos;s own permission, under rules built around the privacy of family
        proceedings, and it is a genuinely more technical step. Given that the council should
        already hold copies of the Letter Before Proceedings, the threshold statement, and the
        order itself — the documents that actually explain "why" — it is worth treating the court
        file as a last resort for whatever is still missing after the easier routes, not a
        starting point. Our{' '}
        <Link to="/guides/how-to-request-your-cafcass-and-family-court-records" className="text-brand underline hover:opacity-90">
          Cafcass and family court records guide
        </Link>{' '}
        covers exactly how, and how hard, that route is.
      </p>
    </GuideSection>

    <GuideSection eyebrow="Common questions" title="Questions people ask us most" tinted>
      <div className="space-y-2" data-testid="why-taken-into-care-faq">
        <GuideFaqItem
          q="What if I was never subject to a care order, just accommodated voluntarily?"
          a="The same council request still applies, and the reasoning should still be written down — referral and assessment records, and the agreement itself, which should record why voluntary accommodation (often called being 'looked after' under section 20) was considered necessary. There's no threshold statement in this case, since that's specific to a court application, but the assessment records serve the same purpose."
        />
        <GuideFaqItem
          q="The reasons given don't match what I remember or what I was told at the time. What do I do?"
          a="That's common, and worth taking seriously rather than assuming your memory is wrong. See our guide on what to do if something in your records is wrong, or was written unkindly."
        />
        <GuideFaqItem
          q="Can I get this information if I was very young when I came into care and don't remember any of it?"
          a="Yes — your right of access doesn't depend on what you remember. Give the council what you do know (an approximate age, an area, any names), and let the records fill in the rest."
        />
        <GuideFaqItem
          q="Is the threshold statement the same as the final judgment?"
          a="No — the threshold statement is what the council puts forward at the start of proceedings, setting out the facts it intends to prove. The judge's final judgment, if one was given in detail, is a separate document explaining what the court actually found and decided, and sits within the harder-to-reach court file rather than routinely being something the council holds its own copy of."
        />
      </div>
    </GuideSection>

    <GuideSection eyebrow="Read more" title="Where this comes from">
      <p>
        The legal test for a care order is{' '}
        <SourceLink href="https://www.legislation.gov.uk/ukpga/1989/41/section/31">
          section 31 of the Children Act 1989
        </SourceLink>
        . Police emergency powers are in{' '}
        <SourceLink href="https://www.legislation.gov.uk/ukpga/1989/41/section/46">
          section 46
        </SourceLink>
        . The Letter Before Proceedings and the Threshold Statement are both defined in{' '}
        <SourceLink href="https://www.justice.gov.uk/courts/procedure-rules/family/practice_directions/pd_part_12a">
          Practice Direction 12A
        </SourceLink>{' '}
        to the Family Procedure Rules 2010, which sets out the Public Law Outline that structures
        every care case.
      </p>
    </GuideSection>

    <GuideCTA
      heading="Add every organisation that might hold a piece of this"
      body="Council, police, Cafcass — add each one you think was involved and we'll draft a separate request for every organisation, so nothing about why decisions were made about your life stays in only one place you never asked."
      testIdPrefix="guide-cta-end"
    />
  </GuideLayout>
);

export default WhyTakenIntoCare;
